// Install missing built-ins before app initialization.
import './polyfills'
import './bootstrap'
import '../css/app.css'

// Import GTM Performance Optimizer
import './gtm-optimizer'


import { createApp, defineAsyncComponent, h } from 'vue'
import { vTrack } from '@/composables/usePageTracking'
import {createInertiaApp, router, usePage} from '@inertiajs/vue3'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import { ZiggyVue } from 'ziggy-js';
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
// import Layout from '@/Layouts/MasterLayout.vue'
import Layout from '@/Layouts/MasterLayoutV2.vue'
import { notifyBugsnag, startBugsnag } from '@/utils/bugsnag'
import { initPageviewTracking } from '@/Helpers/analytics'

// What is deliberately NOT imported here any more (NEX-9357 slow-4G pass on the shared entry):
//  - vue-tailwind-datepicker and @vuepic/vue-datepicker: ~300 KB of source, zero users in any
//    Vue page (the admin report entries under resources/js/admin bundle their own).
//  - PrimeVue + its Tailwind passthrough preset: no page imports a primevue component; only the
//    non-functional ssr.js still registers it.
//  - FontAwesome svg-core + icons: only Home, Reels and VerticalClips render icons, so the library
//    is an async component (plugins/fontawesome.js) prefetched for those pages below.
//  - @bugsnag/js + @bugsnag/plugin-vue + @bugsnag/browser-performance: ~98 KB, now behind
//    @/utils/bugsnag, which dynamically imports them on idle (NEX-9195) and buffers anything
//    reported before they land.
// Removing all four took ~250 KB (gzipped ~90 KB) off every Inertia page's first load.

/**
 * Pages whose first paint includes <font-awesome-icon>s. Their icon chunk is fetched alongside
 * the page component instead of on first render, so nothing pops in a frame late. Every other
 * page still gets the icons — on demand, the first time one renders (a Toast, a share popup).
 */
const FA_PREFETCH_PAGES = /^(Home|Reels|VerticalClips)\//
const loadFontAwesome = () => import('./plugins/fontawesome.js')

const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'Laravel'

// Members area only (app2.blade emits the meta on members2 routes): titles follow
// master.blade's convention — "<page> - Naughty America Members", a title that already
// names the brand as-is, and the bare suffix when a page sets none.
const membersTitleSuffix = document.querySelector('meta[name="members-title-suffix"]')?.content || null
const membersTitle = title => {
    if (!title) return membersTitleSuffix

    return /Naughty America/i.test(title) ? title : `${title} - ${membersTitleSuffix}`
}
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

// The Typekit stylesheet (neue-haas-grotesk) is now a non-blocking <link> in app2.blade.php's
// head, so the font request starts with the HTML instead of after this bundle has executed.

// Bugsnag (error + performance monitoring) is lazy-loaded via @/utils/bugsnag to
// keep ~98KB out of the eager bundle that gates first interaction. Warm it during
// idle so it's ready before most errors occur; anything raised earlier is buffered
// by the util and flushed on init, so no reports are lost.
if ('requestIdleCallback' in window) {
    requestIdleCallback(() => startBugsnag(), { timeout: 4000 })
} else {
    setTimeout(() => startBugsnag(), 1500)
}

/**
 * Forward stylesheet load failures recorded by app2.blade.php's inline <head> watcher.
 *
 * Read after `load` so the watcher's own load-time sweep (links that never errored but never
 * got a sheet) has run. One report per page view, listing every failed href.
 */
function reportCssLoadFailures() {
    const failures = window.__cssLoadFailures || []
    if (!failures.length) {
        return
    }

    notifyBugsnag(new Error(`Stylesheet failed to load: ${failures[0].href}`), (event) => {
        event.severity = 'error'
        event.context = 'css-load'
        event.addMetadata('css_load', { failures, path: window.location.pathname })
    })
}

if (document.readyState === 'complete') {
    setTimeout(reportCssLoadFailures, 0)
} else {
    window.addEventListener('load', () => setTimeout(reportCssLoadFailures, 0), { once: true })
}

/**
 * Members area: a REFRESH always lands at the top of the page.
 *
 * Inertia deliberately restores the previous scroll offset on a reload — see
 * InitialVisit.handleDefault() in @inertiajs/core, which calls
 * `Scroll.restore(history.getScrollRegions())` when `navigationType.isReload()`. The
 * offsets it replays live in `window.history.state` (`documentScrollPosition` for the
 * document, `scrollRegions` for any [scroll-region] elements), which the browser hands
 * back intact across a refresh. Inertia also sets `history.scrollRestoration = 'manual'`
 * on import, so the browser's own restoration is NOT involved and toggling it changes
 * nothing here.
 *
 * Rather than race Inertia's restore with a scrollTo of our own — it runs inside a
 * requestAnimationFrame after an awaited page.set(), so any "scroll to top afterwards"
 * hook is timing-dependent — drop the saved offsets BEFORE createInertiaApp reads them.
 * Inertia then restores {top: 0, left: 0} and no regions, which IS the top of the page.
 *
 * Scoped to Members/* on purpose: this entry is shared with the tour Inertia pages
 * (Reels, StarShop, VerticalClips), whose feeds rely on returning a reader to their
 * place. Back/forward is untouched everywhere — that path is handleBackForward(), a
 * different branch, and this only runs when the navigation type is literally 'reload'.
 *
 * NOTE: v3 puts the initial payload in a `<script data-page>` element (the attribute is
 * the mount-point id, the JSON is its textContent) — `#app[data-page]` reads as the
 * string "app" and would throw here.
 */
function dropScrollOffsetsOnMembersReload() {
    if (typeof window === 'undefined' || !window.history?.state) {
        return
    }

    if (window.performance?.getEntriesByType('navigation')[0]?.type !== 'reload') {
        return
    }

    let component = ''
    try {
        component = JSON.parse(document.querySelector('script[data-page]')?.textContent || '{}').component || ''
    } catch {
        return
    }

    if (!component.startsWith('Members/')) {
        return
    }

    const { scrollRegions, documentScrollPosition, ...state } = window.history.state

    if (scrollRegions === undefined && documentScrollPosition === undefined) {
        return
    }

    window.history.replaceState(state, '')
}

dropScrollOffsetsOnMembersReload()

createInertiaApp({
    title: title => {
        if (membersTitleSuffix) return membersTitle(title)

        const fullTitle = `${title} - ${appName}`
        return fullTitle
    },
    resolve: name =>
        Promise.all([
            resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
            FA_PREFETCH_PAGES.test(name) ? loadFontAwesome().catch(() => null) : null,
        ]).then(
            ([page]) => {
                page.default.layout = page.default.layout || Layout

                return page
            },
        ),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) })
            .use(plugin)
            .use(ZiggyVue)
            .use(pinia)
            // Same tag every template already uses; the library arrives with the first render.
            .component('font-awesome-icon', defineAsyncComponent(loadFontAwesome))
            // NEX-9403 page engagement tracking: v-track="{ section, element, … }".
            .directive('track', vTrack)

        // Forward Vue component errors to (lazy) Bugsnag — replaces the Bugsnag Vue
        // plugin, which we dropped to keep Bugsnag out of the eager bundle. notifyBugsnag
        // buffers until Bugsnag has loaded, so early errors are still captured.
        app.config.errorHandler = (err, instance, info) => {
            notifyBugsnag(err instanceof Error ? err : new Error(String(err)), event => {
                event.addMetadata('vue', { info })
            })
            if (import.meta.env.DEV) {
                console.error(err)
            }
        }

        // Global right-click handler (production only)
        app.config.globalProperties.$disableRightClick = event => {
            try {
                const isProduction = props?.initialPage?.props?.app?.env === 'production'
                if (isProduction) {
                    if (event && typeof event.preventDefault === 'function') {
                        event.preventDefault()
                    }
                    return false
                }
            } catch (e) {
                // no-op
            }
            return true
        }

        app.mount(el)
    },
    progress: {
        // The delay after which the progress bar will appear, in milliseconds...
        delay: 400,

        // The color of the progress bar...
        color: '#0052cc',

        // Whether to include the default NProgress styles...
        includeCSS: true,

        // Whether the NProgress spinner will be shown...
        showSpinner: false,
    },
})

// A promotion just paid this member stars (members3/partials/promotion_grant_notice.blade.php).
// That notice is plain Blade JS outside the Vue app, so it cannot touch page props; every Vue
// balance reads auth.user.wallet_balance, so refresh just `auth` in place.
window.addEventListener('promotion-grant-notice:granted', () => {
    router.reload({ only: ['auth'] })
})

// The last GET page visit Inertia started, for the bot-challenge fallback below.
let lastGetVisitUrl = null
let lastVisitId = null
router.on('before', event => {
    const visit = event.detail.visit
    if (visit.prefetch) return
    lastVisitId = visit.id
    lastGetVisitUrl = visit.method === 'get' ? String(visit.url) : null
})
// Only an in-flight visit may be retried as a page load: a prefetch that fails after the
// visit finished must not reload the page the member is reading. 'finish' runs after the
// httpException/networkError events, so those still see the URL.
router.on('finish', event => {
    if (event.detail.visit?.id !== lastVisitId) return
    lastVisitId = null
    lastGetVisitUrl = null
})

// Global event listener for router error handling.
// Inertia v3 renamed the 'invalid' event to 'httpException'. The event detail's
// `response` is now an HttpExceptionResponse (status, data, headers) from Inertia's
// built-in XHR client — there is no longer a `statusText` field (it was axios-only).
router.on('httpException', event => {
    // AWS WAF answers a request it wants to challenge with a 202 challenge page
    // (x-amzn-waf-action: challenge). Only a real page load can run it, so load the
    // page the member asked for: they pass the challenge and land there, instead of
    // getting an error toast and a click that did nothing.
    //
    // Cloudflare challenges too, as a 403 "Just a moment..." page marked
    // `cf-mitigated: challenge` — the same dead click on a search submit (prod, 2026-10-06).
    const challengeHeaders = event.detail.response?.headers || {}
    const isChallenge = Boolean(challengeHeaders['x-amzn-waf-action'])
        || challengeHeaders['cf-mitigated'] === 'challenge'
    if (isChallenge && lastGetVisitUrl) {
        event.preventDefault()
        window.location.href = lastGetVisitUrl
        return
    }

    const response = event.detail.response
    console.log('Invalid Inertia Response Received:', response)

    const page = usePage()
    const isProduction =
        page.props?.app?.env === 'production' || page.props?.app?.isProduction === true

    // An Inertia response with an error status is the server's own Error page (Handler.php
    // renders Error.vue for 404/403/429/5xx). Let Inertia show it, as Blade shows its 404
    // page. Preventing it here left the member on the old page with a toast — two clicks on
    // a dead performer link, nothing happening (prod, 2026-10-07).
    if (response?.headers?.['x-inertia']) {
        reportHttpException(response, 'warning', `inertia-error-page-${response.status}`)
        return
    }

    // Anything else is not from our app: a Cloudflare 502 "Bad gateway", a plain 403 from an
    // edge rule. A GET visit becomes the full page load Blade would have made, so the member
    // sees what the server actually answered instead of a click that did nothing.
    reportHttpException(response, 'error', `inertia-non-inertia-${response?.status}`)

    if (!isProduction) return // Keep Inertia's modal with the raw response for developers.

    event.preventDefault()
    if (lastGetVisitUrl) {
        window.location.href = lastGetVisitUrl
        return
    }

    window.dispatchEvent(
        new CustomEvent('show-toast', {
            detail: {
                message: 'An unexpected error occurred. Please try again.',
                icon: 'fas fa-circle-info',
                duration: 5000,
            },
        }),
    )
})

/**
 * Report a failed Inertia visit, grouped by what failed rather than as one catch-all.
 *
 * @param {{ status?: number, headers?: object, data?: unknown }} response
 * @param {'error'|'warning'|'info'} severity
 * @param {string} groupingHash
 */
function reportHttpException(response, severity, groupingHash) {
    if (!import.meta.env.VITE_BUGSNAG_API_KEY) return

    notifyBugsnag(new Error(`Inertia visit failed: HTTP ${response?.status}`), function (bugsnagEvent) {
        bugsnagEvent.severity = severity
        bugsnagEvent.groupingHash = groupingHash
        bugsnagEvent.addMetadata('request', {
            url: window.location.href,
            visitUrl: lastGetVisitUrl,
        })
        // v3: response.data may be a string or a parsed object.
        const body =
            typeof response?.data === 'string'
                ? response.data.substring(0, 1000)
                : JSON.stringify(response?.data)?.substring(0, 1000) || null
        bugsnagEvent.addMetadata('response', {
            status: response?.status,
            headers: response?.headers || {},
            body: body,
        })
    })
}

// The request never got an answer (connection dropped, navigation torn down). Inertia
// rejects the visit and the click silently does nothing — 56 HttpNetworkError events in a
// day (prod, 2026-10-07). On Blade the same click is a page load, so make it one.
router.on('networkError', event => {
    if (!lastGetVisitUrl) return
    event.preventDefault()
    window.location.href = lastGetVisitUrl
})

axios.interceptors.response.use(
    response => response,
    error => {
        if (error.response?.status === 401) {
            const intendedUrl = window.location.pathname + window.location.search

            window.location.href = '/login' + intendedUrl ? '?dest=' + intendedUrl : ''
        }
        return Promise.reject(error)
    },
)

// LTI pageview on every Inertia visit (the SDK only counts full page loads itself).
initPageviewTracking()

// Real-User Monitoring of Core Web Vitals (LCP/INP/CLS + FCP/TTFB) → Tinybird.
// Only in built bundles (import.meta.env.PROD) — the dev server's unbundled,
// unminified, no-opcache numbers are misleading, so we don't pollute RUM with them.
// Lazy-imported so web-vitals lands in its own chunk, off the eager boot path.
if (import.meta.env.PROD) {
    import('@/utils/webVitals')
        .then(({ initWebVitals }) => initWebVitals())
        .catch(() => {
            /* RUM is best-effort; never block the app */
        })
}
